import config from './config';
import { createHttp, createClient } from 'sipgate-rest-api-client';

const throwError = (response) => {
	const error = new Error(response.statusText);
	error.response = response;
	error.status = response.status;
	throw error;
};

function isRequestNotFailed(response) {
	return response.status >= 200 && response.status < 300;
}

const checkResponseStatus = (response) => {
	if (isRequestNotFailed(response)) {
		return response;
	}

	throwError(response);
	return null;
};

// called before response body is extracted
const onPromiseResolved = (response) => {
	checkResponseStatus(response);
	return response;
};

export const getClient = (token) => {
	return createClient(createHttp(config.api.url, token, onPromiseResolved));
};

const tokenlessApiClient = createHttp(config.api.url, null, onPromiseResolved);

const getFormData = (object) => {
	let formData = '';
	Object.keys(object)
		.forEach((key) => {
			formData += key + '=' + encodeURIComponent(object[key]) + '&';
		});

	return formData.substring(0, formData.length - 1);
};

const postForm = (data = {}) => {
	return fetch(config.identity.url, {
		method: 'post',
		body: getFormData(data),
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded',
		},
	})
		.then((response) => {
			if (isRequestNotFailed(response)) {
				return response;
			}
			return response.json().then((json) => {
				throw new Error(JSON.stringify(json));
			});
		})
		.then(res => res.json());
};

const getPseudoLinkElement = (url) => {
	const element = window.document.createElement('a');
	element.href = url;
	return element;
};

const allowedDomain = (url) => {
	if (!url || url.indexOf('https://') !== 0) {
		return false;
	}
	const sipgateDomains = [
		'sipgate.de',
		'sipgate.com',
		'sipgate.co.uk',
		'sipgateteam.de',
		'sipgatebasic.de',
		'sipgatebasic.co.uk',
		'simquadrat.de',
		'netzquadrat.de',
		'clinq.com',
	];
	const element = getPseudoLinkElement(url);
	const isAllowedDomain = sipgateDomains.some(domain => domain === element.hostname.substr(-domain.length));
	if (!isAllowedDomain) {
		console.warn(url + ' is not an allowed redirect domain');
	}
	return isAllowedDomain;
};

const getPort = (port) => {
	return /^(80|443)$/.test(port) || !port.length ? '' : ':' + port;
};

// from https://developer.mozilla.org/en-US/docs/Web/API/Location#Examples
const getRedirectUrl = (decodedUrl, accessToken, refreshToken) => {
	const element = getPseudoLinkElement(decodedUrl);
	let search = element.search;

	if (!search) {
		search = `?token=${accessToken}`;
	} else {
		search += `&token=${accessToken}`;
	}

	search += `&refresh_token=${refreshToken}`;

	return `${element.protocol}//${element.hostname}` +
		`${getPort(element.port)}` +
		`${element.pathname.indexOf('/') === 0 ? '' : '/'}` +
		`${element.pathname}` +
		`${search}` +
		`${element.hash}`;
};

const getAuthenticateUrl = (apiClient, linksJson, redirectAfterLogin, accessToken, refreshToken) => {
	const { tokenAuthenticateUrl } = linksJson;
	let redirectTo;

	if (!redirectAfterLogin) {
		redirectTo = `${tokenAuthenticateUrl}${accessToken}`;
		redirectTo += `&refresh_token=${refreshToken}`;
	} else {
		const decodedUrl = decodeURI(redirectAfterLogin);
		if (allowedDomain(decodedUrl)) {
			redirectTo = getRedirectUrl(decodedUrl, accessToken, refreshToken);
		} else {
			redirectTo = '/?failed';
		}
	}

	return redirectTo;
};

const createSession = (username, password, redirectAfterLogin = null, dispatch) => {
	return postForm({
		username,
		password,
		client_id: 'sipgate-app-web',
		grant_type: 'password',
	}, dispatch)
		.then((json) => {
			const accessToken = json.access_token;
			const apiClient = getClient(accessToken);
			const refreshToken = json.refresh_token;
			apiClient.fetchLinks().then((linksJson) => {
				window.location.href = getAuthenticateUrl(
					apiClient,
					linksJson,
					redirectAfterLogin,
					accessToken,
					refreshToken
				);
			});
		});
};

const resetPassword = (username) => {
	return tokenlessApiClient.post('/passwordreset', { username });
};

const changePassword = (nonce, password, passwordRepeat) => {
	return tokenlessApiClient.post('/passwordreset/' + nonce, { password, passwordRepeat });
};

const apiClient = {
	createSession,
	changePassword,
	resetPassword,
};

export default apiClient;



// WEBPACK FOOTER //
// ./src/api/index.js